import InputField from "@components/common/InputField";
import { CustomSelect } from "@components/common/Select";
import { 
	Phone, 
	Badge, 
	Business, 
	LocationOn, 
	Language, 
	Work, 
	Tag,
	Person,
	PhoneAndroid,
	HomeWork,
	Email,
	Public,
	Category
} from "@mui/icons-material";
import { Controller } from "react-hook-form";
import type { ProfileFormFieldsProps } from "../types";
import { Typography, Divider, Box } from "@mui/material";

const ProfileFormFields: React.FC<ProfileFormFieldsProps> = ({ control, sectors, employerTypes }) => {
	return (
		<Box sx={{ overflowX: "hidden", width: "100%" }}>
			{/* Business Details Section */}
			<Typography variant="subtitle1" sx={{ fontWeight: 600, color: "var(--tvet-primary, #2a9699)", mb: 2 }}>
				Business Details
			</Typography>
			
			<div className="grid grid-cols-2 gap-4 mb-4">
				<Controller
					name="businessName"
					control={control}
					render={({ field, fieldState }) => (
						<InputField
							id="profile-businessName"
							label="Name of Company / Firm"
							placeholder="Name of Company / Firm"
							type="text"
							startIcon={<Business className="mr-2" />}
							error={!!fieldState.error}
							helperText={fieldState.error?.message}
							required
							value={field.value || ""}
							onChange={field.onChange}
							onBlur={field.onBlur}
							name={field.name}
						/>
					)}
				/>
				<Controller
					name="officeLandlineNumber"
					control={control}
					render={({ field, fieldState }) => (
						<InputField
							id="profile-officeLandlineNumber"
							label="Office Landline Number"
							placeholder="Office Landline Number"
							type="tel"
							startIcon={<Phone className="mr-2" />}
							error={!!fieldState.error}
							helperText={fieldState.error?.message}
							required
							value={field.value || ""}
							onChange={field.onChange}
							onBlur={field.onBlur}
							name={field.name}
						/>
					)}
				/>
			</div>

			<div className="grid grid-cols-2 gap-4 mb-4">
				<Controller
					name="websiteURL"
					control={control}
					render={({ field, fieldState }) => (
						<InputField
							id="profile-websiteURL"
							label="Website/URL"
							placeholder="Website/URL"
							type="text"
							startIcon={<Language className="mr-2" />}
							error={!!fieldState.error}
							helperText={fieldState.error?.message}
							value={field.value || ""}
							onChange={field.onChange}
							onBlur={field.onBlur}
							name={field.name}
						/>
					)}
				/>
				<Controller
					name="principalLineOfBusiness"
					control={control}
					render={({ field, fieldState }) => (
						<InputField
							id="profile-principalLineOfBusiness"
							label="Principal Line of Business"
							placeholder="Principal Line of Business"
							type="text"
							startIcon={<Work className="mr-2" />}
							error={!!fieldState.error}
							helperText={fieldState.error?.message}
							required
							value={field.value || ""}
							onChange={field.onChange}
							onBlur={field.onBlur}
							name={field.name}
						/>
					)}
				/>
			</div>

			<Controller
				name="registeredOfficeAddress"
				control={control}
				render={({ field, fieldState }) => (
					<InputField
						id="profile-registeredOfficeAddress"
						label="Registered Office Address"
						placeholder="Registered Office Address"
						type="text"
						startIcon={<LocationOn className="mr-2" />}
						error={!!fieldState.error}
						helperText={fieldState.error?.message}
						required
						value={field.value || ""}
						onChange={field.onChange}
						onBlur={field.onBlur}
						name={field.name}
					/>
				)}
			/>

			<div className="mt-4">
				<Controller
					name="sector"
					control={control}
					render={({ field, fieldState }) => (
						<CustomSelect
							id="profile-sector"
							label="Sector"
							placeholder="Select Industry"
							options={sectors || []}
							selected={field.value || ""}
							setSelected={field.onChange}
							error={!!fieldState.error}
							helperText={fieldState.error?.message}
							startIcon={<Category className="mr-2" />}
							required
						/>
					)}
				/>
			</div>

			<Divider sx={{ my: 3 }} />

			{/* Contact Person Details Section */}
			<Typography variant="subtitle1" sx={{ fontWeight: 600, color: "var(--tvet-primary, #2a9699)", mb: 2 }}>
				Contact Person Details
			</Typography>

			<div className="grid grid-cols-2 gap-4 mb-4">
				<Controller
					name="personFullName"
					control={control}
					render={({ field, fieldState }) => (
						<InputField
							id="profile-personFullName"
							label="Full Name"
							placeholder="Full Name"
							type="text"
							startIcon={<Person className="mr-2" />}
							error={!!fieldState.error}
							helperText={fieldState.error?.message}
							required
							value={field.value || ""}
							onChange={field.onChange}
							onBlur={field.onBlur}
							name={field.name}
						/>
					)}
				/>
				<Controller
					name="personCnic"
					control={control}
					render={({ field, fieldState }) => (
						<InputField
							id="profile-personCnic"
							label="CNIC"
							placeholder="CNIC"
							type="text"
							startIcon={<Badge className="mr-2" />}
							error={!!fieldState.error}
							helperText={fieldState.error?.message}
							required
							value={field.value || ""}
							onChange={field.onChange}
							onBlur={field.onBlur}
							name={field.name}
						/>
					)}
				/>
			</div>

			<div className="grid grid-cols-2 gap-4 mb-4">
				<Controller
					name="personLandline"
					control={control}
					render={({ field, fieldState }) => (
						<InputField
							id="profile-personLandline"
							label="Landline Number"
							placeholder="Landline Number"
							type="tel"
							startIcon={<Phone className="mr-2" />}
							error={!!fieldState.error}
							helperText={fieldState.error?.message}
							value={field.value || ""}
							onChange={field.onChange}
							onBlur={field.onBlur}
							name={field.name}
						/>
					)}
				/>
				<Controller
					name="personMobile"
					control={control}
					render={({ field, fieldState }) => (
						<InputField
							id="profile-personMobile"
							label="Mobile Number"
							placeholder="Mobile Number"
							type="tel"
							startIcon={<PhoneAndroid className="mr-2" />}
							error={!!fieldState.error}
							helperText={fieldState.error?.message}
							required
							value={field.value || ""}
							onChange={field.onChange}
							onBlur={field.onBlur}
							name={field.name}
						/>
					)}
				/>
			</div>

			<div className="grid grid-cols-2 gap-4 mb-4">
				<Controller
					name="personEmail"
					control={control}
					render={({ field, fieldState }) => (
						<InputField
							id="profile-personEmail"
							label="Personal Email"
							placeholder="Personal Email"
							type="email"
							startIcon={<Email className="mr-2" />}
							error={!!fieldState.error}
							helperText={fieldState.error?.message}
							required
							value={field.value || ""}
							onChange={field.onChange}
							onBlur={field.onBlur}
							name={field.name}
						/>
					)}
				/>
				<Controller
					name="businessEmailAddress"
					control={control}
					render={({ field, fieldState }) => (
						<InputField
							id="profile-businessEmailAddress"
							label="Business Email"
							placeholder="Business Email"
							type="email"
							startIcon={<Email className="mr-2" />}
							error={!!fieldState.error}
							helperText={fieldState.error?.message}
							required
							value={field.value || ""}
							onChange={field.onChange}
							onBlur={field.onBlur}
							name={field.name}
						/>
					)}
				/>
			</div>

			<Controller
				name="personAddress"
				control={control}
				render={({ field, fieldState }) => (
					<InputField
						id="profile-personAddress"
						label="Personal Address"
						placeholder="Personal Address"
						type="text"
						startIcon={<HomeWork className="mr-2" />}
						error={!!fieldState.error}
						helperText={fieldState.error?.message}
						value={field.value || ""}
						onChange={field.onChange}
						onBlur={field.onBlur}
						name={field.name}
					/>
				)}
			/>

			<Divider sx={{ my: 3 }} />

			{/* KYC Details Section */}
			<Typography variant="subtitle1" sx={{ fontWeight: 600, color: "var(--tvet-primary, #2a9699)", mb: 2 }}>
				KYC Details
			</Typography>

			<Controller
				name="employerType"
				control={control}
				render={({ field, fieldState }) => (
					<CustomSelect
						id="profile-employerType"
						label="Employer Type"
						placeholder="Select Employer Type"
						options={employerTypes || [
							{ value: "national", label: "National" },
							{ value: "international", label: "International" },
						]}
						selected={field.value || ""}
						setSelected={field.onChange}
						error={!!fieldState.error}
						helperText={fieldState.error?.message}
						startIcon={<Public className="mr-2" />}
						required
					/>
				)}
			/>

			<div className="mt-4">
				<Controller
					name="ntn"
					control={control}
					render={({ field, fieldState }) => (
						<InputField
							id="profile-ntn"
							label="Tax Number"
							placeholder="e.g., 1234567-8"
							type="text"
							startIcon={<Tag className="mr-2" />}
							error={!!fieldState.error}
							helperText={fieldState.error?.message}
							required
							value={field.value || ""}
							onChange={field.onChange}
							onBlur={field.onBlur}
							name={field.name}
						/>
					)}
				/>
			</div>
		</Box>
	);
};

export default ProfileFormFields;
